import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const TermsConditions = ({ openModal }) => {
  return (
    <div className="legal-page">
      <header className="legal-hero">
        <div className="legal-hero-inner">
          <nav className="legal-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="bc-separator">/</span>
            <span>Terms and Conditions</span>
          </nav>
          <span className="legal-badge">Terms of Service & Clinical Agreement</span>
          <h1>Terms and Conditions</h1>
          <p className="legal-hero-lead">
            Welcome to Embrace Beauty Aesthetics. Please review the following terms and conditions governing the use of our website, scheduling platform, and clinic treatments.
          </p>
          <div className="legal-meta">
            <div className="legal-meta-item">
              <span>📅 Effective Date: October 2025</span>
            </div>
            <div className="legal-meta-item">
              <span>📍 Embrace Beauty Aesthetics Clinic</span>
            </div>
          </div>
        </div>
      </header>

      <main className="legal-main">
        <div className="legal-container">
          <article className="legal-card">
            <div className="legal-toc">
              <div className="legal-toc-title">Quick Navigation</div>
              <ul className="legal-toc-list">
                <li><a href="#section-1">1. Acceptance of Terms</a></li>
                <li><a href="#section-2">2. Website Use & Intellectual Property</a></li>
                <li><a href="#section-3">3. Appointments & Cancellations</a></li>
                <li><a href="#section-4">4. Payments, Financing & Refunds</a></li>
                <li><a href="#section-5">5. Clinical Procedures & Consent</a></li>
                <li><a href="#section-6">6. Limitation of Liability</a></li>
                <li><a href="#section-7">7. Governing Law & Jurisdiction</a></li>
              </ul>
            </div>

            <section className="legal-section" id="section-1">
              <div className="legal-section-header">
                <span className="legal-section-num">01</span>
                <h2>Acceptance of Terms</h2>
              </div>
              <p>
                By accessing this website, scheduling a consultation, or receiving aesthetic procedures at Embrace Beauty Aesthetics, you agree to comply with and be bound by these Terms and Conditions, alongside our Privacy Policy and Medical Disclaimer.
              </p>
              <p>
                If you do not agree to these terms, please refrain from using our online booking platform and website services. We reserve the right to revise these terms at any time without prior individual notice.
              </p>
            </section>

            <section className="legal-section" id="section-2">
              <div className="legal-section-header">
                <span className="legal-section-num">02</span>
                <h2>Website Use & Intellectual Property</h2>
              </div>
              <p>
                All content published on this website—including but not limited to branding, logos, trademarks, design layout, copy, images, treatment descriptions, and graphics—is the intellectual property of Embrace Beauty Aesthetics or its licensed affiliates.
              </p>
              <ul className="legal-list">
                <li>You may browse and download content solely for personal, non-commercial reference.</li>
                <li>Reproduction, republication, duplication, or distribution of any materials without written authorization is strictly prohibited.</li>
                <li>You agree not to use the website for unlawful activities or to compromise website security.</li>
              </ul>
            </section>

            <section className="legal-section" id="section-3">
              <div className="legal-section-header">
                <span className="legal-section-num">03</span>
                <h2>Appointments & Cancellations</h2>
              </div>
              <p>
                We value your time and schedule our practitioners to ensure personalized, attentive care for each client. Please respect our appointment guidelines:
              </p>
              <div className="legal-callout">
                <h4>⏰ 24-Hour Cancellation Policy</h4>
                <p>
                  We require at least <strong>24 hours' notice</strong> for appointment cancellations or rescheduling. Cancellations made with less than 24 hours' notice or missed appointments may incur a cancellation fee.
                </p>
              </div>
              <ul className="legal-list">
                <li><strong>Booking Accuracy:</strong> You agree to provide accurate contact and health information when booking.</li>
                <li><strong>Late Arrivals:</strong> If you arrive late for your scheduled appointment, your treatment duration may be shortened to avoid delaying subsequent clients, or your appointment may need to be rescheduled.</li>
              </ul>
            </section>

            <section className="legal-section" id="section-4">
              <div className="legal-section-header">
                <span className="legal-section-num">04</span>
                <h2>Payments, Financing & Refunds</h2>
              </div>
              <p>
                Payment for all medical aesthetic treatments, consultations, and skincare products is due in full at the time services are rendered unless pre-approved financing is arranged.
              </p>
              <ul className="legal-list">
                <li><strong>Accepted Methods:</strong> Major credit cards, debit cards, cash, and third-party financing solutions.</li>
                <li><strong>Financing Options:</strong> We are pleased to offer flexible payment plans through Cherry. Any financing agreement entered into with Cherry is governed by Cherry's specific terms and conditions.</li>
                <li><strong>No Refunds on Services:</strong> Due to the customized nature of clinical aesthetic procedures, treatments completed and consultations provided are strictly non-refundable.</li>
                <li><strong>Skincare Products:</strong> Unopened retail skincare products may be eligible for return or exchange within 14 days of purchase with proof of receipt.</li>
              </ul>
            </section>

            <section className="legal-section" id="section-5">
              <div className="legal-section-header">
                <span className="legal-section-num">05</span>
                <h2>Clinical Procedures & Informed Consent</h2>
              </div>
              <p>
                Before receiving any aesthetic treatment, you will undergo a consultation and must review and execute an Informed Consent document detailing procedure specifics, potential risks, anticipated recovery, and aftercare.
              </p>
              <p>
                Clients are required to disclose all current medical conditions, medications, allergies, and history of previous aesthetic procedures. Embrace Beauty Aesthetics reserves the right to decline treatment if a practitioner deems it medically unsuitable.
              </p>
            </section>

            <section className="legal-section" id="section-6">
              <div className="legal-section-header">
                <span className="legal-section-num">06</span>
                <h2>Limitation of Liability</h2>
              </div>
              <p>
                To the maximum extent permitted by applicable law, Embrace Beauty Aesthetics, its practitioners, directors, and affiliates shall not be liable for any indirect, punitive, incidental, or consequential damages resulting from the use of this website or reliance on website information.
              </p>
              <p>
                While we strive for 100% website uptime and precision in digital communications, we do not guarantee uninterrupted, error-free website functionality.
              </p>
            </section>

            <section className="legal-section" id="section-7">
              <div className="legal-section-header">
                <span className="legal-section-num">07</span>
                <h2>Governing Law & Jurisdiction</h2>
              </div>
              <p>
                These terms and conditions are governed by and construed in accordance with the laws of the applicable jurisdiction, without regard to conflict of law principles. Any legal action or dispute arising from these terms shall be settled exclusively in the designated courts of that jurisdiction.
              </p>
            </section>

            <div className="legal-help-box">
              <h3>Have Questions About Our Terms?</h3>
              <p>
                Feel free to contact our clinic team if you have any questions regarding our terms of service, payment options, or booking policies.
              </p>
              <div className="legal-help-actions">
                <button className="btn-primary" onClick={openModal}>Make Appointment</button>
                <a href="tel:+14694341574" className="btn-outline">Call +1 469-434-1574</a>
                <Link to="/" className="btn-outline">Back to Home</Link>
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
};

export default TermsConditions;
